import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { MaskedLines } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/data";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-black">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <video
          data-testid="hero-video"
          autoPlay
          muted
          loop
          playsInline
          poster="/hero-poster.jpg"
          className="w-full h-full object-cover object-center grayscale opacity-60"
        >
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
      </motion.div>

      {/* giant stroke word */}
      <motion.div style={{ opacity: fade }} className="absolute inset-0 flex items-end justify-end pointer-events-none">
        <span className="font-display text-stroke leading-[0.8] text-[26vw] pr-4 select-none translate-y-[6vw]">DVG</span>
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 h-full max-w-[1500px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-[10vh]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-[#cbd1d8]" />
          <span className="text-[12px] uppercase tracking-[0.32em] text-[#cbd1d8]">Athlete Representation</span>
        </motion.div>

        <MaskedLines
          lines={["DAVID", "VS. GOLIATH."]}
          className="font-display leading-[0.82] text-white text-[19vw] sm:text-[16vw] lg:text-[13vw]"
          delay={0.35}
          stagger={0.14}
        />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
          className="mt-8 flex flex-col lg:flex-row lg:items-end gap-8 lg:justify-between"
        >
          <p className="text-white/70 text-lg max-w-xl leading-relaxed">
            DVG Sports empowers athletes and families to win the business of sports through trusted representation, strategic negotiation, and a relentless David vs. Goliath mindset — maximizing value today while protecting the future.
          </p>
          <div className="flex items-center gap-4 shrink-0">
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollTo("contact")}
              className="bg-[#cbd1d8] text-black font-bold uppercase tracking-[0.14em] text-sm px-8 py-4 hover:bg-white transition-colors duration-300"
            >
              Work With Us
            </button>
            <button
              data-testid="hero-cta-team"
              onClick={() => navigate("/team")}
              className="border border-white/25 text-white font-bold uppercase tracking-[0.14em] text-sm px-8 py-4 hover:border-[#cbd1d8] hover:text-[#cbd1d8] transition-colors duration-300"
            >
              Meet The Team
            </button>
          </div>
        </motion.div>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <ArrowDown size={18} className="text-white/50 animate-bounce" />
      </motion.div>
    </section>
  );
};
